import { useCallback, useState } from 'react';
import { Icon } from '../components/Decor';
import { ARTICLE_CAT_LABEL, CLIP_CAT_LABEL, thaiDate, youtubeId, type Article, type Clip, type NewsItem } from '../shared/types';
import { Link } from './router';
import { useModalDialog } from './dialog';
import artHoro from '../assets/art/art-horo.svg';
import artTrade from '../assets/art/art-trade.svg';
import artMind from '../assets/art/art-mind.svg';
import artRisk from '../assets/art/art-risk.svg';
import artLive from '../assets/art/art-live.svg';
import artCosmos from '../assets/art/art-cosmos.svg';

// Vector brand art (CI navy/purple/gold, sharp at every size) used when the admin has not set a cover image.
const FALLBACK: Record<string, string> = { horo: artHoro, psychology: artMind, risk: artRisk, trade: artTrade, live: artLive, news: artCosmos };

export const DemoBadge = ({ on }: { on?: boolean }) => (on ? <span className="demo-badge">ข้อมูลตัวอย่าง</span> : null);
export const LockBadge = ({ on }: { on?: boolean }) => (on ? <span className="lock-badge"><Icon name="lock" /> เฉพาะสมาชิก</span> : null);

function VideoDialog({ clip, onClose }: { clip: Clip; onClose: () => void }) {
  const ref = useModalDialog(onClose);
  const id = youtubeId(clip.url);
  return (
    <dialog ref={ref} className="policy-dialog video-dialog" aria-labelledby="video-title">
      <div className="policy-head">
        <h2 id="video-title" className="h3">{clip.title}</h2>
        <button type="button" className="btn btn-icon" aria-label="ปิด" onClick={onClose}><Icon name="close" /></button>
      </div>
      {id && (
        <div className="video-frame">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={clip.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      )}
      {clip.desc && <p className="muted video-desc">{clip.desc}</p>}
    </dialog>
  );
}

export function ClipCard({ clip, onLocked }: { clip: Clip; onLocked: () => void }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const yt = youtubeId(clip.url);
  const thumb = clip.thumb || (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : FALLBACK[clip.category]);
  const play = () => {
    if (clip.locked) return onLocked();
    if (yt) return setOpen(true);
    if (clip.url) window.open(clip.url, '_blank', 'noopener,noreferrer');
  };
  const playable = clip.locked || !!clip.url;
  return (
    <article className="cms-card clip-card">
      <button type="button" className="cms-thumb" onClick={play} disabled={!playable} aria-label={clip.locked ? `${clip.title} — เข้าสู่ระบบสมาชิกเพื่อดู` : playable ? `เล่นคลิป ${clip.title}` : `${clip.title} — ยังไม่มีวิดีโอ`}>
        <img src={thumb} alt="" loading="lazy" />
        <span className="play"><Icon name={clip.locked ? 'lock' : 'play'} /></span>
      </button>
      <div className="cms-body">
        <p className="cms-meta"><span>{CLIP_CAT_LABEL[clip.category]}</span> · <time dateTime={clip.date}>{thaiDate(clip.date, false)}</time></p>
        <h3 className="h3">{clip.title}</h3>
        {clip.desc && <p className="muted small clamp">{clip.desc}</p>}
        <p className="cms-tags"><LockBadge on={clip.membersOnly} /><DemoBadge on={clip.demo} />{!clip.url && !clip.locked && <span className="fine">ยังไม่มีวิดีโอ</span>}</p>
      </div>
      {open && <VideoDialog clip={clip} onClose={close} />}
    </article>
  );
}

export function ArticleCard({ item }: { item: Article }) {
  return (
    <article className="cms-card">
      <Link to={`/articles/${item.id}`} className="cms-thumb" label={item.title}>
        <img src={item.cover || FALLBACK[item.category]} alt="" loading="lazy" />
      </Link>
      <div className="cms-body">
        <p className="cms-meta"><span>{ARTICLE_CAT_LABEL[item.category]}</span> · <time dateTime={item.date}>{thaiDate(item.date, false)}</time></p>
        <h3 className="h3"><Link to={`/articles/${item.id}`}>{item.title}</Link></h3>
        {item.excerpt && <p className="muted small clamp">{item.excerpt}</p>}
        <p className="cms-tags"><LockBadge on={item.membersOnly} /><DemoBadge on={item.demo} /></p>
      </div>
    </article>
  );
}

export function NewsCard({ item }: { item: NewsItem }) {
  const first = item.body.split('\n').find((l) => l.trim() && !l.startsWith('## ') && !/^ข่าวตัวอย่าง/.test(l)) ?? '';
  return (
    <article className="cms-card news-item">
      {item.cover && (
        <Link to={`/news/${item.id}`} className="cms-thumb" label={item.title}>
          <img src={item.cover} alt="" loading="lazy" />
        </Link>
      )}
      <div className="cms-body">
        <p className="cms-meta">
          {item.pinned && <span className="pin-badge">ปักหมุด</span>}
          <time dateTime={item.date}>{thaiDate(item.date)}</time>
        </p>
        <h3 className="h3"><Link to={`/news/${item.id}`}>{item.title}</Link></h3>
        {first && <p className="muted small clamp">{first}</p>}
        <p className="cms-tags"><DemoBadge on={item.demo} /></p>
      </div>
    </article>
  );
}
